import React from 'react'
import * as THREE from 'three'
import { Vector3, Vector3Like } from 'three'
import {
  GeckoBinSize,
  GeckoComponentCoords,
  GeckoComponentDataFrame,
  GeckoComponentModel,
  GeckoComponentRect,
  GeckoFixedSensor,
  GeckoLegacyBoilerWall,
  GeckoManway,
  GeckoMarkupPrimitive,
  GeckoNozzle,
  GeckoPlate,
  GeckoShellCourse,
  GeckoShipDeckBitt,
  GeckoShipDeckChock,
  GeckoShipDeckCoaming,
  GeckoShipDeckHatch,
  GeckoWeld,
} from 'types'

//------------------------------------------------------------------------------

/** A simple representation of a Vector2 */
export type Vec2 = [number, number]

/** A simple representation of a Vector3 */
export type Vec3 = [number, number, number]

/** A simple representation of a Vector4 */
export type Vec4 = [number, number, number, number]

//------------------------------------------------------------------------------

/** The anchor point of an HtmlOverlay component (aligns to the point in space) */
export type HtmlOverlayAnchor =
  | 'left'
  | 'right'
  | 'bottom'
  | 'top'
  | 'center'
  | 'bottom left'
  | 'bottom right'
  | 'top left'
  | 'top right'

/** An enum for describing the relative priority given to overlapping HtmlOverlays */
export enum HtmlOverlayPriority {
  FeatureNamePlate,
  PlateNamePlate,
  WeldNamePlate,
  RulerTickLow,
  RulerTickMedium,
  RulerTickHigh,
  MajorFeatureNamePlate,
  Markup,
}

/** A type for holding all information required to render an HtmlOverlay */
export type HtmlOverlayEntry = {
  id: number
  priority: HtmlOverlayPriority
  screenDepth: number
  isFrustumCulled: boolean
  isOcclusionCulled: boolean | undefined
  left: number
  right: number
  top: number
  bottom: number
  node: React.ReactNode
}

//------------------------------------------------------------------------------

export type ExtrudingFeature =
  | GeckoNozzle
  | GeckoManway
  | GeckoFixedSensor
  | GeckoWeld
  | GeckoShipDeckChock
  | GeckoShipDeckBitt
  | GeckoShipDeckCoaming
  | GeckoShipDeckHatch

/** A type for holding information about the state of the cursor */
export type MouseState = {
  // 2D (screen) coordinates
  mouseX: number
  mouseY: number
  // 3D coordinates
  positionX: number
  positionY: number
  positionZ: number
  // 2D (component) coordinates
  componentX: number
  componentY: number
  componentWrapsX: boolean
  componentWrapsY: boolean
  componentBounds: GeckoComponentRect | undefined
  componentDataFrame: GeckoComponentDataFrame | undefined
  componentModel: GeckoComponentModel | undefined
  plate: GeckoPlate | undefined
  course: GeckoShellCourse | undefined
  wall: GeckoLegacyBoilerWall | undefined
  extrudingFeature: ExtrudingFeature | undefined
  markupPrimitive: GeckoMarkupPrimitive | undefined
}

/** A type for holding information about the camera's center of focus */
export type CameraCoords = {
  componentID: string
  componentX: number
  componentY: number
  distToComponent: number
}

/** A type for holding information about where the camera can or cannot go */
export type CameraBounds = {
  rect: GeckoComponentRect
  doWrapX?: boolean
  doWrapY?: boolean
  distToComponentMin?: number
  distToComponentMax?: number
}

//------------------------------------------------------------------------------

/** A type for holding information about a component surface, passed back on hover */
export type ComponentSurfaceMeshData = {
  bounds: GeckoComponentRect
  doWrapX: boolean
  doWrapY: boolean
  componentModel?: GeckoComponentModel
}

export type MarkupPrimitiveMeshData = {
  primitives: readonly GeckoMarkupPrimitive[]
  indexes: readonly number[]
}
/** A type for holding information about a hovered primitive */
export type MarkupPrimitiveData = {
  markupPrimitives: MarkupPrimitiveMeshData | undefined
}

//------------------------------------------------------------------------------

/**
 * A method which converts component coords into (u, v) coordinates within a
 * component's bounds (or the bounds of a section of a component, such as a
 * shell course)
 * @param coords The component coords to turn into (u, v) coordinates
 * @param bounds The bounds to get (u, v) coordinates on
 */
export const convertCoordsToUVs = (
  coords: GeckoComponentCoords,
  bounds: GeckoComponentRect
): Vec2 => {
  const convertedCoords = coords.project(bounds.space)
  return [
    convertedCoords.x.minus(bounds.start.x).num / bounds.width.num,
    convertedCoords.y.minus(bounds.start.y).num / bounds.height.num,
  ]
}

/**
 * A function which takes a rect defined by two points and snaps it to the
 * nearest grid lines, wrapping around a component's bounds in the X- and Y-
 * directions if allowed and necessary.
 * @param x1           The x value of the start point of this rect
 * @param y1           The y value of the start point of this rect
 * @param x2           The x value of the end point of this rect
 * @param y2           The y value of the end point of this rect
 * @param wrapX        Whether this component's surface wraps in the x-direction
 * @param wrapY        Whether this component's surface wraps in the y-direction
 * @param bounds       The bounds of this component (potentially to wrap around)
 * @param binning      The bin-size to snap to
 * @param dataGeometry The geometry of the data, so the bins snap to the data-coords
 */
export const snapToBins = (
  x1: number,
  y1: number,
  x2: number,
  y2: number,
  wrapX: boolean,
  wrapY: boolean,
  bounds: GeckoComponentRect,
  binning: GeckoBinSize,
  dataGeometry: GeckoComponentRect
): GeckoComponentRect => {
  // Figure out the start and end bin measurement, wrapping if possible
  const swapX = wrapX && Math.abs(x1 - x2) > bounds.width.num / 2
  const startX = swapX ? Math.max(x1, x2) : Math.min(x1, x2)
  const endX = swapX ? Math.min(x1, x2) : Math.max(x1, x2)

  const swapY = wrapY && Math.abs(y1 - y2) > bounds.height.num / 2
  const startY = swapY ? Math.max(y1, y2) : Math.min(y1, y2)
  const endY = swapY ? Math.min(y1, y2) : Math.max(y1, y2)

  const xUnit = bounds.x.unit
  const xWrapStart = bounds.x.start.num
  const xWrapEnd = bounds.x.end.num
  const binWidth = binning.width.convertTo(xUnit).num
  const cdfStartX = dataGeometry.start.x.convertTo(xUnit).num
  let snappedStartX =
    Math.floor((startX - cdfStartX) / binWidth) * binWidth + cdfStartX
  let snappedEndX =
    Math.ceil((endX - cdfStartX) / binWidth) * binWidth + cdfStartX
  if (wrapX) {
    snappedStartX = wrapIfNecessary(snappedStartX, xWrapStart, xWrapEnd)
    snappedEndX = wrapIfNecessary(snappedEndX, xWrapStart, xWrapEnd)
  }

  const yUnit = bounds.y.unit
  const yWrapStart = bounds.y.start.num
  const yWrapEnd = bounds.y.end.num
  const binHeight = binning.height.convertTo(yUnit).num
  const cdfStartY = dataGeometry.start.y.convertTo(yUnit).num
  let snappedStartY =
    Math.floor((startY - cdfStartY) / binHeight) * binHeight + cdfStartY
  let snappedEndY =
    Math.ceil((endY - cdfStartY) / binHeight) * binHeight + cdfStartY
  if (wrapY) {
    snappedStartY = wrapIfNecessary(snappedStartY, yWrapStart, yWrapEnd)
    snappedEndY = wrapIfNecessary(snappedEndY, yWrapStart, yWrapEnd)
  }

  return GeckoComponentRect.CreateFromXYs(
    bounds.componentID,
    snappedStartX,
    snappedStartY,
    snappedEndX,
    snappedEndY,
    xUnit,
    yUnit
  )
}

/**
 * A special type of wrapping function which returns a value between start and
 * end ONLY if the value is out of range.
 * @param t       The value to wrap around the provided value range
 * @param start   The start of the range
 * @param end     The end of the range
 */
const wrapIfNecessary = (t: number, start: number, end: number) =>
  t === end
    ? t
    : THREE.MathUtils.euclideanModulo(t - start, end - start) + start

export const extractScale = (
  scale:
    | number
    | [number, number, number]
    | Readonly<[number, number, number]>
    | Vector3Like
    | Readonly<Vector3Like>
    | undefined
): Vector3 => {
  if (scale === undefined) return new Vector3(1, 1, 1)
  if (typeof scale === 'number') return new Vector3(scale, scale, scale)
  if (Array.isArray(scale)) return new Vector3(scale[0], scale[1], scale[2])
  const vec3 = scale as Vector3Like
  return new Vector3(vec3.x, vec3.y, vec3.z)
}
